import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand, FAQ, SectionHeader } from "@/components/site/Blocks";
import { publishedInvestorCities } from "@/data/investor-cities";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import bestRentalMarketsHero from "@/assets/hero/hero-best-rental-markets-family-research.jpg";

const FAQS = [
  {
    q: "What makes a good rental market for investors?",
    a: "Four things in combination: a rent-to-price ratio that supports debt service, population and job growth that keeps units leased, a landlord-tenant framework with a workable eviction timeline, and property tax plus insurance costs that do not consume the spread. A high cap rate in a shrinking market is not a good market.",
  },
  {
    q: "Which markets cash flow best right now?",
    a: "Cash flow generally concentrates in Midwest and Southeast metros where median prices remain moderate relative to rents, while coastal and mountain-west metros price more of the return into appreciation. Rather than rely on a national list, compare the actual median value, 2-bedroom rent, and cap-rate band for each market on the city pages below.",
  },
  {
    q: "How do I compare two rental markets objectively?",
    a: "Line them up on median home value, median 2-bedroom rent, HUD Fair Market Rent for the county, the local cap-rate band, property tax rate, insurance cost, and landlord-law posture. Our city comparison tool does this side by side so the decision is not driven by a single headline metric.",
  },
  {
    q: "Do DSCR lenders restrict which markets they will lend in?",
    a: "Yes. Availability varies by state and by property type, and some programs limit rural properties, declining markets, or unusually small unit sizes. We are licensed in a defined set of states — check the where-we-lend page for the current footprint. Availability, rates, and terms are subject to change and full underwriting.",
  },
  {
    q: "Should I buy in a market I have never visited in person?",
    a: "Many investors do, but it raises the diligence bar: verify neighborhood-level crime and school data, confirm the property manager's local track record, and have an independent inspection and appraisal in hand before committing. Remote purchases work best when you have a trusted boots-on-the-ground team already in place.",
  },
  {
    q: "Do property taxes vary enough between markets to change the return?",
    a: "Yes, significantly. Effective property tax rates can differ by more than a full percentage point of value between states and even between counties within the same metro, which directly reduces net cash flow. Always underwrite using the actual current assessed tax bill for the specific parcel, not a metro-wide average.",
  },
  {
    q: "How does landlord-tenant law affect financing or market choice?",
    a: "Jurisdictions with slower eviction timelines or stricter rent-control rules generally carry more operating risk, which some underwriting factors into vacancy and reserve assumptions. It does not change loan eligibility directly, but it is worth weighing alongside cap rate when comparing two otherwise similar markets.",
  },
  {
    q: "What is a realistic cap rate to target for a DSCR-financed rental?",
    a: "There is no single national benchmark — cap rates that clear 1.0x-plus DSCR at current pricing vary by market, property type, and leverage. Compare the property's projected cap rate against local comparable sales rather than a generic target percentage.",
  },
  {
    q: "Can I finance a rental in a market outside where I currently live?",
    a: "Yes. Business-purpose investment property financing does not require the borrower to reside in or near the subject property's market, only that the property state is one we currently service. Check the where-we-lend footprint before making an offer.",
  },
  {
    q: "How much does insurance cost eat into returns in coastal or high-risk markets?",
    a: "In wind, hail, or flood-exposed metros, insurance can represent a meaningfully larger share of operating expenses than in inland markets, sometimes enough to flip an attractive cap rate into a marginal one. Get a bindable insurance quote for the specific address before finalizing your offer and DSCR underwriting.",
  },
  {
    q: "How does population growth data help predict rental demand?",
    a: "Metros with sustained net in-migration and job growth tend to see steadier rent growth and lower vacancy over a multi-year hold, since housing supply typically lags demand growth. Pair population trend data with local permitting activity to see whether new supply is likely to offset that demand.",
  },
  {
    q: "Should I prioritize appreciation potential or cash flow when picking a market?",
    a: "It depends on your investment goals and how the loan is structured — a DSCR loan qualifies on in-place or market rent, so weak near-term cash flow in a high-appreciation market can still limit what you can borrow today. Many investors blend both by holding cash-flow-positive properties in stable markets alongside a smaller allocation to higher-growth markets.",
  },
];

export const Route = createFileRoute("/best-rental-markets")({
  head: () =>
    buildHead({
      title: "Best Rental Markets for Investors — Metro-by-Metro Data",
      description:
        "Compare the best rental property markets across the states we lend in: median values, 2-bedroom rents, cap-rate bands, demand drivers and DSCR financing for each metro.",
      url: "/best-rental-markets",
      keywords:
        "best rental markets, best places to buy rental property, best cash flow markets for real estate, top rental property markets, best cities for DSCR loans, rental market cap rates by city, best real estate investing markets, rent to price ratio by city, investor metro comparison, where to buy rental property",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Best Rental Markets", url: "/best-rental-markets" },
      ],
      service: { name: "Investor Rental Market Research" },
      faqs: FAQS,
    }),
  component: BestMarketsPage,
});

function BestMarketsPage() {
  const [q, setQ] = useState("");

  const byState = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? publishedInvestorCities.filter(
          (c) => c.name.toLowerCase().includes(needle) || c.stateName.toLowerCase().includes(needle),
        )
      : publishedInvestorCities;
    const map = new Map<string, typeof publishedInvestorCities>();
    for (const c of filtered) {
      const list = map.get(c.stateName) ?? [];
      list.push(c);
      map.set(c.stateName, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [q]);

  const total = publishedInvestorCities.length;
  const stateCount = new Set(publishedInvestorCities.map((c) => c.stateSlug)).size;

  return (
    <SiteShell>
      <PageHero
        eyebrow="Market research"
        title="Best Rental Markets for Investors"
        subtitle={`Median values, two-bedroom rents, cap-rate bands and demand drivers for ${total} investor metros across ${stateCount} states where we finance business-purpose rental property. Market data is illustrative research, not investment advice or an offer of credit.`}
        primaryHref="/apply?product=dscr-loans"
        primaryLabel="Get Your Personalized Rate Quote"
        secondaryHref="/tools/compare-cities"
        secondaryLabel="Compare Cities Side by Side"
        image={{
          src: bestRentalMarketsHero,
          alt: "Family investors reviewing rental-market data across a neighborhood of rental properties",
        }}
      />
      <PageEssentials />

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 text-sm text-foreground/90">
          <SectionHeader
            title="How to read a rental market"
            intro="Cash flow is a function of four variables, and headline cap rate is only one of them."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { t: "Rent-to-price", b: "The starting screen. Higher rent relative to median value means the payment is easier to cover, which is what a DSCR lender is measuring." },
              { t: "Demand durability", b: "Job and population growth, employer concentration, and university or hospital anchors decide whether the rent holds in a soft year." },
              { t: "Carry costs", b: "Property tax rate at the reassessed value and current insurance pricing move net income more than a quarter-point of rate does." },
              { t: "Landlord framework", b: "Eviction timelines, notice periods, and rent-regulation posture determine your downside when a tenancy goes wrong." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="font-display text-base font-semibold text-foreground">{c.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{c.b}</p>
              </div>
            ))}
          </div>
          <p>
            Once a market passes those four screens, the deal-level work is the same everywhere: run{" "}
            <Link to="/dscr-calculator" className="text-brand underline">DSCR</Link> on the actual rent,{" "}
            <Link to="/cap-rate-calculator" className="text-brand underline">cap rate</Link> on a real expense load, and{" "}
            <Link to="/cash-on-cash-return-calculator" className="text-brand underline">cash-on-cash</Link> on the cash you
            actually bring.
          </p>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-6xl">
          <SectionHeader
            title="Investor metros we finance"
            intro="Each market links to a full local guide with rents, HUD Fair Market Rents, landlord rules and DSCR financing detail."
          />
          <div className="mt-6">
            <label className="block max-w-md">
              <span className="mb-1 block text-sm font-medium text-foreground">Search a city or state</span>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Tampa, Georgia, Memphis…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-base outline-none focus:border-brand"
              />
            </label>
          </div>

          {byState.length === 0 && (
            <p className="mt-8 text-sm text-muted-foreground">
              No markets match that search. Try a state name, or see the full{" "}
              <Link to="/where-we-lend" className="text-brand underline">lending footprint</Link>.
            </p>
          )}

          <div className="mt-8 space-y-10">
            {byState.map(([stateName, cities]) => (
              <div key={stateName}>
                <h3 className="font-display text-xl font-bold text-foreground">{stateName}</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {cities.map((c) => (
                    <Link
                      key={`${c.stateSlug}-${c.slug}`}
                      to="/dscr-loans/$state/$city"
                      params={{ state: c.stateSlug, city: c.slug }}
                      className="block rounded-xl border border-border bg-card p-5 shadow-sm transition hover:border-brand-gold hover:shadow-md"
                    >
                      <div className="font-display text-lg font-semibold text-foreground">
                        {c.name}, {stateName}
                      </div>
                      <dl className="mt-3 space-y-1 text-sm">
                        <div className="flex justify-between gap-3">
                          <dt className="text-muted-foreground">Median value</dt>
                          <dd className="tabular-nums text-foreground">{c.medianHomeValue}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-muted-foreground">Median 2BR rent</dt>
                          <dd className="tabular-nums text-foreground">{c.medianRent2BR}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-muted-foreground">Cap-rate band</dt>
                          <dd className="tabular-nums text-foreground">{c.capRateBand}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-muted-foreground">Population</dt>
                          <dd className="tabular-nums text-foreground">{c.population}</dd>
                        </div>
                      </dl>
                      {c.strengths?.[0] && (
                        <p className="mt-3 text-xs text-muted-foreground">{c.strengths[0]}</p>
                      )}
                      <span className="mt-3 inline-block text-sm font-semibold text-brand">View market guide →</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="Rental market FAQ" intro="How investors should choose where to buy." />
          <div className="mt-6">
            <FAQ items={FAQS} />
          </div>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Research tools</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/tools/compare-cities" as const, label: "Compare investor cities side by side" },
              { to: "/hud-fair-market-rents" as const, label: "HUD Fair Market Rents by county" },
              { to: "/section-8-investing-map" as const, label: "Section 8 investing map" },
              { to: "/cap-rate-calculator" as const, label: "Cap rate calculator" },
              { to: "/cash-on-cash-return-calculator" as const, label: "Cash-on-cash return calculator" },
              { to: "/where-we-lend" as const, label: "Where we lend" },
            ].map((l) => (
              <li key={l.to}><Link to={l.to} className="text-brand underline">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      </Section>

      <CTABand
        title="Found your market?"
        body="Send us the address or the scenario and we'll return an illustrative DSCR structure for it — business-purpose investment property only, subject to full underwriting."
      />
    <PageOutro />
      </SiteShell>
  );
}
